import React, { useEffect, useMemo, useState } from "react";
import { useSetLoading } from "../../../common/hooks/appearance";
import {
  AlertComponent,
  BreadcrumbComponent,
  SearchInputComponent,
} from "../../../common/components";
import { Box } from "@mui/material";
import MUITable from "../../../common/components/mui-table/mui-table";
import moment from "moment";
import PaginationComponent from "../../../common/components/pagination-component/pagination-component";
import "../requests/tenant-requests.scss";
import _ from "lodash";
import {
  SortOption,
  RequestsList,
  RequestsListParams,
  breadcrumbLink,
  getPOCName,
} from "../requests/tenant-requests-helper";
import { getTenets } from "../../../api/tenets";
import AddNewTenant from "../add-tenant/add-new-tenant";
import { convertUTCToLocal, isValidPhoneNumber } from "../../../utils/utils";

const TenantRequests: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);
  const [requestList, setRequestList] = useState<RequestsList>({
    totalRecords: null,
    data: [],
  });
  const [sortOption, setSortOption] = useState<SortOption>({
    sortDirection: null,
    sortBy: null,
  });
  const [alert, setAlert] = useState<{
    isOpen?: boolean;
    message?: any;
    type?: string;
  }>({});
  const toggle = () => {
    setIsOpen(false);
    setCurrentPage(1);
  };
  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  const headers = useMemo(
    () => [
      { label: "Company Name", key: "company_name", isSortable: true },
      {
        label: "POC Name",
        key: "pocName",
        isSortable: true,
        valueGetter: (row: any) => getPOCName(row.TenantPrimaryUsers),
      },
      {
        label: "Email",
        key: "email",
        isSortable: true,
        valueGetter: (row: any) => row?.TenantPrimaryUsers?.email || "-",
      },
      {
        label: "Phone",
        key: "phone_number",
        isSortable: true,
        valueGetter: (row: any) =>
          isValidPhoneNumber(row?.TenantPrimaryUsers?.phone_number)
            ? row?.TenantPrimaryUsers?.phone_number
            : "-",
      },
      {
        label: "Date & Time",
        key: "created_at",
        isSortable: true,
        valueGetter: (row: any) =>
          convertUTCToLocal(row?.created_at, "DD/MM/YY hh:mm A"),
      },
      {
        label: "Add",
        key: "",
        valueGetter: (row: any) => {
          return (
            <span
              onClick={() => {
                setSelectedRow(row);
                setIsOpen(true);
              }}
              className="add-partner-span"
            >
              Add Partner
            </span>
          );
        },
      },
    ],
    []
  );

  const getTenantRequestsList = async (
    page: number = 1,
    search: string = ""
  ) => {
    try {
      setIsLoading(true);

      const offset = (page - 1) * pageSize;
      let params: RequestsListParams = {
        limit: pageSize,
        offset: offset,
        status: ["REQUESTED_ACCESS"],
        isRequestAccess: true,
      };
      if (sortOption?.sortBy) {
        params = { ...params, ...sortOption };
      }
      if (search) {
        params.searchText = search;
      }
      const response = await getTenets(params);
      setRequestList(response?.data?.data?.data);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.error(e);
    }
  };
  const handleSort = (column: string, sortType: string) => {
    let sortValues: SortOption = { sortBy: null, sortDirection: null };
    if (sortType !== "Unsort") {
      sortValues = { sortDirection: sortType.toLowerCase(), sortBy: column };
    }
    setCurrentPage(1);
    setSortOption(sortValues);
  };
  const noResultsTxt = useMemo(() => {
    if (isLoading) {
      return "Loading partners...";
    }
    if (searchText) {
      return "No partners found matching your search criteria.";
    }
    return "No partners found";
  }, [isLoading, searchText]);
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };
  const handleSearch = _.debounce((text: string) => {
    setSearchText(text);
    setCurrentPage(1);
  }, 500);
  useEffect(() => {
    getTenantRequestsList(currentPage, searchText);
  }, [currentPage, searchText, sortOption, isOpen]);
  return (
    <>
      <div className="requests-list-wrapper">
        <div className="breadcrumb-wrapper">
          <BreadcrumbComponent breadcrumbLink={breadcrumbLink} />
        </div>
        <div className="requests-list-filter-wrapper">
          <div className="requests-search-input-wrapper">
            <SearchInputComponent
              placeholder="Search by company name, email etc.,"
              onSearch={handleSearch}
            />
          </div>
        </div>
        <div className="tenant-table-filter">
          <Box className="requests-list-table">
            <MUITable
              headers={headers}
              data={requestList?.data}
              isStriped={false}
              onSort={handleSort}
              noResultsTxt={noResultsTxt}
            />
          </Box>
        </div>
        {requestList?.totalRecords ? (
          <div className="tenant-request-pagination-container">
            <PaginationComponent
              totalRecords={requestList?.totalRecords || 0}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        ) : (
          <></>
        )}
      </div>
      {isOpen && selectedRow && (
        <AddNewTenant
          open={isOpen}
          onClose={toggle}
          setAlert={setAlert}
          rowData={selectedRow}
        />
      )}
      {alert?.isOpen && (
        <AlertComponent
          message={alert?.message || ""}
          type={
            ["info", "success", "error", "warning"].includes(alert?.type || "")
              ? (alert?.type as "info" | "success" | "error" | "warning")
              : "info"
          }
          open={alert?.isOpen}
          onClose={() => setAlert({})}
        />
      )}
    </>
  );
};
export default TenantRequests;
